import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';

import IconInfoOrenge from '../../assets/images/common/icon-info-orange.svg';
import IconMailOrange from '../../assets/images/common/icon-mail-orange.svg';
import IconMailWihte from '../../assets/images/common/icon-mail-wihte.svg';

import { ESTIMATE_API_ERRORS } from '../../types/api/EstimateApiType';
import { CI_INPUT, MAIL_ADDRESS_MAX_LENGTH } from '../../types/common/CustomerInformationType';
import { ErrorMessages } from '../../types/common/Messages';
import { TEMP_SAVE_PATH_LIST } from '../../types/common/CommonType';

import { ErrorModalModal } from '../../components/ErrorModal';
import { env_user } from '../../conf/environ_user';
import { estimateApi } from '../../api/estimate';
import { checkLength, checkMailAddressFormat } from '../../utils/stringCheckUtil';
import { ErrorMessage } from '../../components/ErrorMessage';


type TemporarilySavedModalProps = {
    openState: boolean;
    setOpenState: (value: boolean) => void;
    draftId: string;
    tempSaveType: string;
};

export const TemporarilySavedModal: React.FC<TemporarilySavedModalProps> = ({ openState, setOpenState, draftId, tempSaveType }) => {
    // メールアドレス入力内容制御
    const [inputMailAddress, setInputMailAddress] = useState<string>('');
    // メール送信済
    const [mailSended, setMailSended] = useState<boolean>(false);
    // URLコピー結果
    const [copyResult, setCopyResult] = useState<string>('');
    // エラーメッセージ管理
    const [errorMessages, setErrorMessages] = useState<Array<string>>([]);
    // エラーモーダル開閉管理
    const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
    const [modalErrorMessage, setModalErrorMessage] = useState<Array<string>>([]);
    // ボタン実行管理
    const [buttonPush, setButtonPush] = useState<boolean>(false);

    // 復帰時のパス
    const draftPath = TEMP_SAVE_PATH_LIST.find(it=>it.type == tempSaveType)?.path ?? '';
    // アクセスURL
    const accessUrl = env_user.draft_acceess_url + draftPath + draftId;

    // 各項目初期化
    useEffect(() => {
        setInputMailAddress('');
        setMailSended(false);
        setErrorMessages([]);
    }, [openState]);

    // クリップボードにコピー
    const copyToClipboard = () => {
        navigator.clipboard.writeText(accessUrl).then(
            function () {
                setCopyResult('コピーしました');
                setTimeout(() => {
                    setCopyResult('');
                }, 2000);
            },
            function () {
                setCopyResult('');
            }
        );
    };

    // メール送信
    const clickSendMail = () => {
        const sendMail = async () => {
            // setStateの遅延考慮
            let errorResult = false;
            const addErrorMessages: Array<string> = [];

            // メール送信処理前バリデーションチェック
            if (inputMailAddress.length === 0) {
                // 必須入力チェック
                errorResult = true;
                addErrorMessages.push(ErrorMessages.TEMP_MAIL_VAL02);
                setErrorMessages(addErrorMessages);
            } else {
                // 桁数チェック
                const lengthErrorMessage = checkLength(inputMailAddress, CI_INPUT.MAIL, MAIL_ADDRESS_MAX_LENGTH);
                if (lengthErrorMessage != '') {
                    errorResult = true;
                    addErrorMessages.push(lengthErrorMessage);
                    setErrorMessages(addErrorMessages);
                }
                // 形式チェック
                if (!checkMailAddressFormat(inputMailAddress)) {
                    errorResult = true;
                    addErrorMessages.push(ErrorMessages.TEMP_MAIL_VAL01);
                    setErrorMessages(addErrorMessages);
                }
            }
            if (!errorResult) {
                setButtonPush(true);
                // エラー無しであれば送信API実施
                const apiResult = await estimateApi.saveDraftEstimateMail(draftPath, draftId, inputMailAddress);
                if (apiResult === true) {
                    setMailSended(true);
                } else {
                    setModalErrorMessage([
                        (apiResult as string).includes(ESTIMATE_API_ERRORS.REQUEST_ERROR_5003)
                            ? ErrorMessages.API5003_SEND_ERROR.replace('%1', apiResult as string)
                            : ErrorMessages.API5003_SEND_ERROR_OTHER,
                    ]);
                    setOpenErrorModal(true);
                }
                setButtonPush(false);
            }
        };
        // エラーメッセージ初期化
        setErrorMessages([]);
        if (!buttonPush) {
            void sendMail();
        }
    };

    return (
        <Modal
            isOpen={openState}
            onAfterOpen={() => setOpenState(true)}
            onRequestClose={() => setOpenState(false)}
            className={'modal-card'}
            id={'modal-save'}
            overlayClassName={'modal-overlay'}
            ariaHideApp={false}
        >
            <header className='modal-card-head'>
                <p className='modal-card-title'>一時保存</p>
                <button className='delete' aria-label='close' onClick={() => setOpenState(false)}>
                    <span>CLOSE</span>
                </button>
                <p className='modal-card-subtitle'>save</p>
            </header>
            <section className='modal-card-body'>
                <div className='content'>
                    <div className='columns'>
                        <div className='column'>
                            <p className='has-text-centered has-text-weight-bold has-text-dl-blue'>
                                機器情報・お見積り情報に入力頂いた内容を一時保存しました。
                                <br className='is-block-mobile' />
                                保存期間は30日です。
                            </p>
                            <h3>復元方法</h3>
                            <div className='modal_info'>
                                <p className='has-text-centered-tablet'>
                                    下記のURLをテキストファイル等に保存してください。
                                    <br />
                                    発行されたURLよりアクセスしていただくと、保存した内容を復元出来ます。
                                </p>
                                <p className='has-text-centered-tablet'>
                                    また、一時保存で発行されたURLは指定されたメールアドレスに送信することもできます。
                                </p>
                                <div className='field is-horizontal'>
                                    <div className='field-body'>
                                        <div className='field is-grouped'>
                                            <p className='control is-expanded url_clip'>
                                                <input className='input' type='text' value={accessUrl} readOnly />
                                            </p>
                                            <p className='control-clip'>
                                                <a
                                                    className='button is-small is-primary'
                                                    onClick={() => copyToClipboard()}
                                                >
                                                    クリップボードにコピー
                                                </a>
                                                {copyResult !== '' && (
                                                    <div className='copy-text'>
                                                        <span className='icon'>
                                                            <img src={IconInfoOrenge} alt='' />
                                                        </span>
                                                        <span className='text'>{copyResult}</span>
                                                    </div>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='field is-horizontal'>
                                <div className='field-body'>
                                    <div className='field is-grouped sendmail-wrapper'>
                                        <p className='info-text'>メールアドレスへ転送する方はこちら</p>
                                        <p className='control has-icons-left'>
                                            <input
                                                className='input'
                                                type='email'
                                                value={inputMailAddress}
                                                placeholder='メールアドレスを入力してください'
                                                onChange={(e) => setInputMailAddress(e.target.value)}
                                                disabled={mailSended}
                                            />
                                            <span className='icon is-small is-left'>
                                                <img src={IconMailOrange} alt='' />
                                            </span>
                                        </p>
                                        <p className='control'>
                                            {!mailSended ? (
                                                <a
                                                    className='button is-small is-dl_gra'
                                                    onClick={() => clickSendMail()}
                                                >
                                                    <span>送信する</span>
                                                    <span className='icon'>
                                                        <img src={IconMailWihte} alt='' />
                                                    </span>
                                                </a>
                                            ) : (
                                                <button className='button is-small is-dl_gray'>
                                                    <span>送信しました</span>
                                                    <span className='icon'>
                                                        <img src={IconMailWihte} alt='' />
                                                    </span>
                                                </button>
                                            )}
                                        </p>
                                        {errorMessages &&
                                            errorMessages.map((errorMessage, index) => (
                                                <ErrorMessage
                                                    errorMessage={errorMessage}
                                                    key={`temp-save-mail-error-${index}`}
                                                />
                                            ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <ErrorModalModal
                openState={openErrorModal}
                setOpenState={setOpenErrorModal}
                errorMessages={modalErrorMessage}
            />
        </Modal>
    );
};
